export const projectsData = [
  {
    id: "rag-bot",
    slug: "ai-knowledge-chatbot",
    title: "AI Knowledge Chatbot (RAG)",
    tagline: "Cut support time 40%",
    tags: ["AI", "RAG", "Support"],
    category: "AI & Machine Learning",
    duration: "4 months",
    role: "Product Lead",
    team: "3 Engineers, 1 Designer, 1 Data Scientist",
    summary: "Built a retrieval-augmented generation (RAG) chatbot that transformed how our support team handles knowledge-base queries, reducing response times by 40%.",
    problem: {
      title: "The Challenge",
      description: "Support teams were overwhelmed by repetitive, knowledge-base-oriented questions. Agents spent 60% of their time on L1 tickets that could be automated, leading to burnout and slow response times.",
      painPoints: [
        "60% of tickets were repetitive knowledge-base queries",
        "Average response time was 4+ hours for simple questions",
        "Support team experiencing burnout from monotonous work",
        "Knowledge base was fragmented across multiple systems"
      ]
    },
    approach: {
      title: "The Approach",
      description: "I led a cross-functional team to design and ship a RAG-powered chatbot that could understand context, retrieve relevant documentation, and provide accurate answers with citations.",
      phases: [
        {
          name: "Discovery & Scoping",
          duration: "2 weeks",
          activities: [
            "Analyzed 10,000+ support tickets to identify automation opportunities",
            "Mapped knowledge base structure and identified gaps",
            "Defined success metrics and guardrails for AI responses"
          ]
        },
        {
          name: "Design & Architecture",
          duration: "3 weeks",
          activities: [
            "Designed retrieval pipeline with semantic search",
            "Created feedback loops for continuous improvement",
            "Built evaluation framework for response quality"
          ]
        },
        {
          name: "Build & Iterate",
          duration: "8 weeks",
          activities: [
            "Implemented RAG architecture with vector embeddings",
            "Developed admin dashboard for knowledge management",
            "Ran A/B tests to optimize response accuracy"
          ]
        },
        {
          name: "Launch & Scale",
          duration: "3 weeks",
          activities: [
            "Phased rollout starting with 10% of traffic",
            "Trained support team on escalation workflows",
            "Set up monitoring and alerting systems"
          ]
        }
      ]
    },
    impact: {
      title: "The Impact",
      description: "The chatbot transformed our support operations, allowing the team to focus on complex issues while providing instant answers for common queries.",
      metrics: [
        { value: "40%", label: "Reduction in support handling time" },
        { value: "70%", label: "Self-serve resolution rate for L1" },
        { value: "4.2/5", label: "User satisfaction score" },
        { value: "50K+", label: "Queries handled monthly" }
      ],
      outcomes: [
        "Reduced median support handling time by ~40%",
        "Increased self-serve resolution rate for L1 tickets to 70%",
        "Unlocked new insights from user queries for product discovery",
        "Support team now focuses on complex, high-value interactions"
      ]
    },
    techStack: ["Python", "LangChain", "OpenAI", "Pinecone", "React", "FastAPI"],
    learnings: [
      "Guardrails are critical — we implemented strict boundaries to prevent hallucinations",
      "User feedback loops dramatically improved response quality over time",
      "Starting with a narrow scope allowed us to ship faster and iterate"
    ],
    zh: {
      title: "AI 知识库问答机器人 (RAG 检索增强)",
      tagline: "客服处理时长缩减 40%",
      tags: ["人工智能", "RAG", "智能客服"],
      category: "AI 与机器学习",
      duration: "4 个月",
      role: "产品负责人 (Product Lead)",
      team: "3 位工程师、1 位设计师、1 位数据科学家",
      summary: "打造了基于检索增强生成 (RAG) 架构的智能客服知识库机器人，彻底重塑了客服团队处理知识库问答的模式，使整体响应时长降低 40%。",
      problem: {
        title: "核心挑战与业务痛点",
        description: "客户服务团队长期被大量重复性、知识库导向的基础问题淹没。人工坐席 60% 的工时消耗在完全可以自动化的 L1 级工单上，导致客服人员疲惫倦怠，客户等待时长居高不下。",
        painPoints: [
          "60% 的服务工单属于重复性的知识库基础查询",
          "即使是极简单的基础问题，平均首答响应时长也超过 4 小时",
          "繁琐机械的工作流程导致客服团队成员流失与倦怠严重",
          "企业内部知识资产碎片化散落在多个独立系统中，缺乏统一治理"
        ]
      },
      approach: {
        title: "解决方案与推行路径",
        description: "我带领跨职能产研团队，从零设计并交付了一款基于 RAG 的智能问答机器人。系统能够深度理解用户语境，毫秒级召回关联文档，并给出带有明确引用出处的精准解答。",
        phases: [
          {
            name: "需求洞察与边界界定",
            duration: "2 周",
            activities: [
              "系统化聚类分析 10,000+ 条历史客服工单，精准定位自动化高频切入点",
              "全面梳理企业知识库架构并完成知识盲区与断点的补齐",
              "确立核心成功指标 (North Star Metric) 及大模型安全防护护栏 (Guardrails)"
            ]
          },
          {
            name: "架构设计与原型打磨",
            duration: "3 周",
            activities: [
              "设计融合语义向量检索与倒排索引的高性能多路召回管线",
              "构建用户负反馈与纠错的闭环回流机制，赋能知识库持续自进化",
              "搭建涵盖准确率、幻觉率、召回率的大模型自动化评测评估基准"
            ]
          },
          {
            name: "工程落地与敏捷迭代",
            duration: "8 周",
            activities: [
              "基于向量嵌入 (Vector Embeddings) 落地高性能 RAG 底层微服务",
              "打造直观易用的知识管理中台看板，方便运营人员随时热更新知识条目",
              "开展多轮严密的 A/B 灰度测试，持续调优 Prompt 与模型召回准确率"
            ]
          },
          {
            name: "全面上线与规模推广",
            duration: "3 周",
            activities: [
              "采取分阶段灰度放量策略，首批切入 10% 流量并平稳放量至全量",
              "对客服一线团队开展工单流转培训与异常情况人工兜底协同训练",
              "建立全天候生产环境监控、Token 消耗预警与质量熔断系统"
            ]
          }
        ]
      },
      impact: {
        title: "项目成效与业务收益",
        description: "该机器人全面升级了客户支持运作体系，使团队能够将精力转向高价值深度疑难问题，同时为数万名用户提供即问即答的秒级响应体验。",
        metrics: [
          { value: "40%", label: "客服综合响应耗时下降" },
          { value: "70%", label: "L1 级工单自助解决率" },
          { value: "4.2/5", label: "终端用户满意度得分" },
          { value: "5万+", label: "每月稳定处理交互问答量" }
        ],
        outcomes: [
          "客服工单平均中位处理时长显著缩减约 40%",
          "L1 基础常见工单的自助完结率跃升至 70%",
          "从用户高频提问中提炼宝贵的用户需求洞察，反哺产品核心规划",
          "客服团队从机械搬运中解放，专注于大客户复杂场景的高价值陪伴"
        ]
      },
      learnings: [
        "护栏机制至关重要 —— 我们设计了极其严格的置信度阈值与兜底逻辑，杜绝大模型臆测幻觉",
        "闭环的用户点赞/点踩反馈通道，是后续大模型答案准确率持续攀升的核心驱动力",
        "在 0→1 阶段收窄业务边界聚焦单一痛点，让团队得以在短短数周内完成上线与验证"
      ]
    },
    vi: {
      title: "Chatbot tri thức AI (RAG)",
      tagline: "Giảm 40% thời gian hỗ trợ",
      tags: ["AI", "RAG", "Hỗ trợ khách hàng"],
      category: "AI & Học máy",
      duration: "4 tháng",
      role: "Trưởng nhóm sản phẩm",
      team: "3 kỹ sư, 1 nhà thiết kế, 1 nhà khoa học dữ liệu",
      summary: "Xây dựng chatbot tạo sinh tăng cường truy xuất (RAG) giúp thay đổi cách đội ngũ hỗ trợ xử lý các câu hỏi về cơ sở tri thức, rút ngắn 40% thời gian phản hồi.",
      problem: {
        title: "Thách thức",
        description: "Đội ngũ hỗ trợ bị quá tải bởi các câu hỏi lặp đi lặp lại liên quan đến cơ sở tri thức. Nhân viên dành 60% thời gian cho các yêu cầu L1 vốn có thể tự động hóa, dẫn đến kiệt sức và phản hồi chậm.",
        painPoints: [
          "60% yêu cầu hỗ trợ là các câu hỏi lặp lại về cơ sở tri thức",
          "Thời gian phản hồi trung bình hơn 4 giờ, kể cả với những câu hỏi đơn giản",
          "Đội ngũ hỗ trợ kiệt sức vì công việc đơn điệu",
          "Cơ sở tri thức bị phân mảnh trên nhiều hệ thống"
        ]
      },
      approach: {
        title: "Cách tiếp cận",
        description: "Tôi dẫn dắt một nhóm liên chức năng thiết kế và ra mắt chatbot ứng dụng RAG, có khả năng hiểu ngữ cảnh, truy xuất tài liệu liên quan và đưa ra câu trả lời chính xác kèm trích dẫn nguồn.",
        phases: [
          {
            name: "Khám phá & xác định phạm vi",
            duration: "2 tuần",
            activities: [
              "Phân tích hơn 10.000 yêu cầu hỗ trợ để xác định cơ hội tự động hóa",
              "Lập bản đồ cấu trúc cơ sở tri thức và xác định các lỗ hổng",
              "Xác định chỉ số thành công và cơ chế kiểm soát (guardrails) cho câu trả lời của AI"
            ]
          },
          {
            name: "Thiết kế & kiến trúc",
            duration: "3 tuần",
            activities: [
              "Thiết kế pipeline truy xuất với tìm kiếm ngữ nghĩa",
              "Xây dựng vòng phản hồi để cải tiến liên tục",
              "Xây dựng khung đánh giá chất lượng câu trả lời"
            ]
          },
          {
            name: "Xây dựng & lặp lại",
            duration: "8 tuần",
            activities: [
              "Triển khai kiến trúc RAG với vector embedding",
              "Phát triển bảng điều khiển quản trị để quản lý tri thức",
              "Chạy thử nghiệm A/B để tối ưu độ chính xác của câu trả lời"
            ]
          },
          {
            name: "Ra mắt & mở rộng",
            duration: "3 tuần",
            activities: [
              "Triển khai theo từng giai đoạn, bắt đầu với 10% lưu lượng",
              "Đào tạo đội ngũ hỗ trợ về quy trình chuyển cấp xử lý",
              "Thiết lập hệ thống giám sát và cảnh báo"
            ]
          }
        ]
      },
      impact: {
        title: "Tác động",
        description: "Chatbot đã thay đổi hoàn toàn hoạt động hỗ trợ, giúp đội ngũ tập trung vào các vấn đề phức tạp trong khi người dùng nhận được câu trả lời tức thì cho những câu hỏi phổ biến.",
        metrics: [
          { value: "40%", label: "Giảm thời gian xử lý hỗ trợ" },
          { value: "70%", label: "Tỷ lệ tự giải quyết yêu cầu L1" },
          { value: "4,2/5", label: "Điểm hài lòng của người dùng" },
          { value: "50.000+", label: "Câu hỏi được xử lý mỗi tháng" }
        ],
        outcomes: [
          "Giảm khoảng 40% thời gian xử lý hỗ trợ trung vị",
          "Nâng tỷ lệ tự giải quyết các yêu cầu L1 lên 70%",
          "Khai thác insight mới từ câu hỏi của người dùng để phục vụ khám phá sản phẩm",
          "Đội ngũ hỗ trợ giờ đây tập trung vào các tương tác phức tạp, giá trị cao"
        ]
      },
      learnings: [
        "Guardrails là yếu tố then chốt — chúng tôi đặt ra giới hạn nghiêm ngặt để ngăn hiện tượng ảo giác (hallucination)",
        "Vòng phản hồi từ người dùng giúp cải thiện đáng kể chất lượng câu trả lời theo thời gian",
        "Bắt đầu với phạm vi hẹp giúp chúng tôi ra mắt nhanh hơn và cải tiến liên tục"
      ]
    }
  },
  {
    id: "kpi-platform",
    slug: "kpi-dashboard-platform",
    title: "Unified KPI Dashboard Platform",
    tagline: "Faster exec decisions",
    tags: ["Analytics", "Platform", "Exec"],
    category: "Data & Analytics",
    duration: "6 months",
    role: "Senior Product Manager",
    team: "5 Engineers, 2 Data Engineers, 1 Designer",
    summary: "Created a single source of truth for company KPIs, eliminating the chaos of fragmented metrics and enabling faster executive decision-making.",
    problem: {
      title: "The Challenge",
      description: "Leadership had fragmented views of metrics across multiple tools and teams. Every business review started with debates about which numbers were correct.",
      painPoints: [
        "5+ different tools used for metrics reporting",
        "30% of meeting time spent reconciling conflicting numbers",
        "No single definition of key business metrics",
        "Data teams overwhelmed with ad-hoc reporting requests"
      ]
    },
    approach: {
      title: "The Approach",
      description: "I led the initiative to create a unified metrics platform with clear ownership, governance, and self-serve capabilities.",
      phases: [
        {
          name: "Stakeholder Alignment",
          duration: "3 weeks",
          activities: [
            "Interviewed 20+ stakeholders across departments",
            "Mapped existing metrics and identified conflicts",
            "Built consensus on canonical definitions"
          ]
        },
        {
          name: "Data Architecture",
          duration: "6 weeks",
          activities: [
            "Designed semantic layer for consistent metrics",
            "Created data contracts with source systems",
            "Built automated data quality checks"
          ]
        },
        {
          name: "Platform Development",
          duration: "10 weeks",
          activities: [
            "Built self-serve dashboard builder",
            "Implemented role-based access control",
            "Created alerting and anomaly detection"
          ]
        },
        {
          name: "Adoption & Training",
          duration: "5 weeks",
          activities: [
            "Ran training sessions for 100+ users",
            "Created documentation and playbooks",
            "Established governance committee"
          ]
        }
      ]
    },
    impact: {
      title: "The Impact",
      description: "The platform became the single source of truth, dramatically reducing time spent on data reconciliation and enabling faster decisions.",
      metrics: [
        { value: "80%", label: "Reduction in reporting requests" },
        { value: "2hrs", label: "Saved per exec per week" },
        { value: "100%", label: "Adoption across leadership" },
        { value: "15min", label: "New dashboard creation time" }
      ],
      outcomes: [
        "Improved decision-making speed for key business reviews",
        "Reduced time spent stitching metrics from multiple systems",
        "Aligned stakeholders on a single definition of success",
        "Data team freed to focus on strategic analysis"
      ]
    },
    techStack: ["dbt", "Snowflake", "Looker", "Airflow", "Great Expectations"],
    learnings: [
      "Governance is as important as technology — clear ownership prevents chaos",
      "Self-serve capabilities dramatically reduce bottlenecks",
      "Starting with executive buy-in made adoption much smoother"
    ],
    zh: {
      title: "统一 KPI 决策看板平台 (Single Source of Truth)",
      tagline: "助力高管层极速开展战略决策",
      tags: ["商业智能", "指标中台", "高管决策"],
      category: "大数据与指标中台",
      duration: "6 个月",
      role: "资深产品经理 (Senior PM)",
      team: "5 位全栈工程师、2 位大数据工程师、1 位产品设计师",
      summary: "打造了企业级统一指标与 KPI 看板平台，彻底根除了各部门口径不一、数据孤岛林立的乱象，为管理层快速决断提供统一真实的数据来源 (Single Source of Truth)。",
      problem: {
        title: "核心挑战与业务痛点",
        description: "高管层与各业务线负责人使用的分析工具割裂严重，各部门报表统计口径冲突频发。每次关键经营分析会往往需要先花费半小时争论哪张报表的数据才是准确的。",
        painPoints: [
          "企业内部共存 5 个以上异构报表系统，口径不一难以对齐",
          "高达 30% 的会议时间被浪费在跨部门对数和口径澄清上",
          "缺乏权威统一的核心业务指标字典与元数据定义规范",
          "数据开发团队疲于应付源源不断的临时取数 (Ad-hoc) 需求"
        ]
      },
      approach: {
        title: "解决方案与推行路径",
        description: "我主导推进了统一指标平台的系统化立项与建设，确立了统一指标所有权归属、指标治理准则，并构建了人人可用的自助式看板分析能力。",
        phases: [
          {
            name: "跨部门利益相关者对齐",
            duration: "3 周",
            activities: [
              "深度访谈销售、运营、财务等跨部门 20+ 位核心业务与数据负责人",
              "地毯式摸排梳理全公司现有报表与指标，排查 100+ 处冲突口径",
              "召集专题对齐会，沉淀并签署第一版权威指标公理定义字典"
            ]
          },
          {
            name: "数据架构与语义层搭建",
            duration: "6 周",
            activities: [
              "引入 Metric Layer 统一语义层，确保底层计算口径全局一致",
              "与各业务源系统制定数据契约 (Data Contracts)，保障上游稳定输出",
              "搭建自动化数据质量校验流水线，异常波动毫秒级拦截告警"
            ]
          },
          {
            name: "平台系统研发与权限管控",
            duration: "10 周",
            activities: [
              "打造拖拽式零代码自助看板生成器，赋能业务人员自由定制图表",
              "落地细粒度基于角色的访问控制 (RBAC) 与数据安全脱敏机制",
              "引入基于时序算法的核心指标异动归因与智能异常监测模块"
            ]
          },
          {
            name: "全员赋能培训与长效治理",
            duration: "5 周",
            activities: [
              "分批开展 100+ 位高管及核心分析师的使用培训与答疑工坊",
              "编写完善的指标开发规范、看板配置指南及标准操作手册 (SOP)",
              "组建指标管理委员会，制定新指标准入、废弃与口径审计长效机制"
            ]
          }
        ]
      },
      impact: {
        title: "项目成效与业务收益",
        description: "该平台已成为全公司高层经营会、各部门周会的唯一指定数据源，极大地压缩了对数沟通成本，推动企业决策全面走向敏捷数字化。",
        metrics: [
          { value: "80%", label: "日常手工临时取数需求减少" },
          { value: "2小时", label: "每位高管每周节省对数时长" },
          { value: "100%", label: "核心管理层周会看板覆盖率" },
          { value: "15分钟", label: "新业务分析看板平均搭建时长" }
        ],
        outcomes: [
          "战略级经营决策会议效率大幅提升，决断周期缩短数天",
          "彻底告别多系统手动拼凑 Excel 报表的数据噩梦",
          "全员在统一的业务语言和 North Star 成功定义上协同作战",
          "数据工程师从被动临时取数中解脱，专注于高价值前瞻性深度分析"
        ]
      },
      learnings: [
        "数据治理的重要性丝毫不亚于技术选型 —— 明确的指标归属人是杜绝混乱的关键",
        "给予业务端充分灵活的自助分析工具，能从根本上消除跨部门研发瓶颈",
        "在项目初期争取到高管层的坚定支持与亲身带头使用，让推广阻力降至最低"
      ]
    },
    vi: {
      title: "Nền tảng dashboard KPI hợp nhất",
      tagline: "Ban lãnh đạo ra quyết định nhanh hơn",
      tags: ["Phân tích", "Nền tảng", "Lãnh đạo"],
      category: "Dữ liệu & Phân tích",
      duration: "6 tháng",
      role: "Quản lý sản phẩm cấp cao",
      team: "5 kỹ sư, 2 kỹ sư dữ liệu, 1 nhà thiết kế",
      summary: "Xây dựng nguồn dữ liệu chuẩn duy nhất cho các KPI của công ty, chấm dứt tình trạng chỉ số phân mảnh và giúp ban lãnh đạo ra quyết định nhanh hơn.",
      problem: {
        title: "Thách thức",
        description: "Ban lãnh đạo có cái nhìn rời rạc về các chỉ số trên nhiều công cụ và nhóm khác nhau. Mọi buổi đánh giá kinh doanh đều bắt đầu bằng cuộc tranh luận xem số liệu nào mới đúng.",
        painPoints: [
          "Hơn 5 công cụ khác nhau được dùng để báo cáo chỉ số",
          "30% thời gian họp dành cho việc đối chiếu các số liệu mâu thuẫn",
          "Không có định nghĩa thống nhất cho các chỉ số kinh doanh quan trọng",
          "Đội ngũ dữ liệu quá tải với các yêu cầu báo cáo đột xuất"
        ]
      },
      approach: {
        title: "Cách tiếp cận",
        description: "Tôi dẫn dắt sáng kiến xây dựng nền tảng chỉ số hợp nhất với quyền sở hữu rõ ràng, cơ chế quản trị chặt chẽ và khả năng tự phục vụ.",
        phases: [
          {
            name: "Thống nhất các bên liên quan",
            duration: "3 tuần",
            activities: [
              "Phỏng vấn hơn 20 bên liên quan thuộc nhiều phòng ban",
              "Lập bản đồ các chỉ số hiện có và xác định điểm mâu thuẫn",
              "Xây dựng đồng thuận về các định nghĩa chuẩn"
            ]
          },
          {
            name: "Kiến trúc dữ liệu",
            duration: "6 tuần",
            activities: [
              "Thiết kế lớp ngữ nghĩa (semantic layer) để đảm bảo chỉ số nhất quán",
              "Thiết lập hợp đồng dữ liệu (data contract) với các hệ thống nguồn",
              "Xây dựng cơ chế kiểm tra chất lượng dữ liệu tự động"
            ]
          },
          {
            name: "Phát triển nền tảng",
            duration: "10 tuần",
            activities: [
              "Xây dựng công cụ tự tạo dashboard",
              "Triển khai kiểm soát truy cập theo vai trò",
              "Xây dựng tính năng cảnh báo và phát hiện bất thường"
            ]
          },
          {
            name: "Triển khai & đào tạo",
            duration: "5 tuần",
            activities: [
              "Tổ chức các buổi đào tạo cho hơn 100 người dùng",
              "Soạn tài liệu hướng dẫn và playbook",
              "Thành lập ủy ban quản trị"
            ]
          }
        ]
      },
      impact: {
        title: "Tác động",
        description: "Nền tảng trở thành nguồn dữ liệu chuẩn duy nhất, giảm mạnh thời gian đối chiếu dữ liệu và giúp ra quyết định nhanh hơn.",
        metrics: [
          { value: "80%", label: "Giảm yêu cầu báo cáo" },
          { value: "2 giờ", label: "Tiết kiệm mỗi tuần cho mỗi lãnh đạo" },
          { value: "100%", label: "Mức độ sử dụng trong ban lãnh đạo" },
          { value: "15 phút", label: "Thời gian tạo dashboard mới" }
        ],
        outcomes: [
          "Tăng tốc độ ra quyết định trong các buổi đánh giá kinh doanh quan trọng",
          "Giảm thời gian ghép nối chỉ số từ nhiều hệ thống",
          "Thống nhất các bên liên quan về một định nghĩa thành công duy nhất",
          "Đội ngũ dữ liệu được giải phóng để tập trung vào phân tích chiến lược"
        ]
      },
      learnings: [
        "Quản trị quan trọng không kém công nghệ — quyền sở hữu rõ ràng giúp tránh hỗn loạn",
        "Khả năng tự phục vụ giúp giảm đáng kể các điểm nghẽn",
        "Có được sự ủng hộ của ban lãnh đạo ngay từ đầu giúp việc triển khai suôn sẻ hơn nhiều"
      ]
    }
  },
  {
    id: "data-quality",
    slug: "data-quality-automation",
    title: "Data Quality Automation",
    tagline: "Reduced failures significantly",
    tags: ["Data", "Quality", "Automation"],
    category: "Data Infrastructure",
    duration: "4 months",
    role: "Product Manager",
    team: "4 Data Engineers, 1 Platform Engineer",
    summary: "Built an automated data quality framework that caught issues before they impacted downstream consumers, reducing critical incidents by 75%.",
    problem: {
      title: "The Challenge",
      description: "Data issues surfaced late in workflows, causing broken reports and mistrust. Teams spent more time firefighting than building.",
      painPoints: [
        "Critical data issues discovered in production reports",
        "No systematic way to detect anomalies early",
        "Unclear ownership when issues occurred",
        "Trust in data declining across the organization"
      ]
    },
    approach: {
      title: "The Approach",
      description: "I designed a proactive data quality framework with automated checks, clear SLAs, and ownership structures.",
      phases: [
        {
          name: "Assessment",
          duration: "2 weeks",
          activities: [
            "Catalogued critical data paths and dependencies",
            "Analyzed incident history to identify patterns",
            "Defined quality dimensions and priorities"
          ]
        },
        {
          name: "Framework Design",
          duration: "3 weeks",
          activities: [
            "Created SLI/SLO framework for data quality",
            "Designed check templates for common patterns",
            "Built escalation and ownership model"
          ]
        },
        {
          name: "Implementation",
          duration: "8 weeks",
          activities: [
            "Deployed automated checks across pipelines",
            "Implemented anomaly detection algorithms",
            "Built alerting and incident management"
          ]
        },
        {
          name: "Operationalization",
          duration: "3 weeks",
          activities: [
            "Created runbooks for common issues",
            "Trained data owners on response procedures",
            "Established weekly quality reviews"
          ]
        }
      ]
    },
    impact: {
      title: "The Impact",
      description: "The framework transformed data quality from reactive firefighting to proactive prevention.",
      metrics: [
        { value: "75%", label: "Reduction in critical incidents" },
        { value: "95%", label: "Issues caught before production" },
        { value: "4hrs", label: "Faster incident resolution" },
        { value: "30%", label: "Increase in data trust scores" }
      ],
      outcomes: [
        "Reduced critical data quality incidents by 75%",
        "Increased trust in analytics for strategic decisions",
        "Freed teams from reactive, ad-hoc data firefighting",
        "Clear ownership model for faster resolution"
      ]
    },
    techStack: ["Great Expectations", "dbt", "Monte Carlo", "PagerDuty", "Slack"],
    learnings: [
      "Prevention is cheaper than cure — early detection saves countless hours",
      "Clear ownership is essential for accountability",
      "Automated runbooks reduce MTTR significantly"
    ],
    zh: {
      title: "数据质量自动化与异常监控平台",
      tagline: "大幅降低数据异常与生产故障",
      tags: ["数据基础设施", "质量保障", "自动化流水线"],
      category: "数据架构与可靠性工程",
      duration: "4 个月",
      role: "数据产品经理 (Product Manager)",
      team: "4 位大数据研发工程师、1 位平台运维工程师",
      summary: "构建了端到端的数据质量自动化校验框架，在脏数据污染下游报表之前完成主动拦截与告警，使关键生产级数据事故锐减 75%。",
      problem: {
        title: "核心挑战与业务痛点",
        description: "数据质量问题往往在流转至末端报表甚至被客户发现后才暴露，导致报表错误频出，业务信任度跌至冰点。工程师每天奔波于被动救火，无法专注核心架构演进。",
        painPoints: [
          "重大脏数据与缺失异常经常在生产环境高管报表上才被察觉",
          "缺乏在上游抽取与中间转换阶段尽早捕获异常的自动化监测体系",
          "事故发生时链路上下游互相推诿，权责归属与排查链路不清",
          "全公司对内部数据分析结论的真实性与严谨性产生普遍信任危机"
        ]
      },
      approach: {
        title: "解决方案与推行路径",
        description: "我设计并落地了一套主动式数据质量保障体系，包含覆盖全链路的自动化校验断言、清晰严格的 SLA/SLO 承诺及责任到人的闭环响应机制。",
        phases: [
          {
            name: "现状摸底与故障复盘",
            duration: "2 周",
            activities: [
              "深度梳理全链路核心数据管道与拓扑依赖关系图谱",
              "复盘过去一年的重大数据事故，总结提炼出 5 类高频诱因模式",
              "确立数据完整性、时效性、唯一性、一致性等核心治理维度"
            ]
          },
          {
            name: "治理框架与规则设计",
            duration: "3 周",
            activities: [
              "为重点业务表量身定制数据质量服务等级目标 (SLI/SLO)",
              "沉淀通用断言模版库（如空值率、主键唯一、数值区间、枚举分布）",
              "建立分级告警响应机制与明确的事故责任人路由模型"
            ]
          },
          {
            name: "系统工程落地与接入",
            duration: "8 周",
            activities: [
              "在 ETL/ELT 流水线关键节点全量埋设自动化校验阻断逻辑",
              "引入时序统计学算法，实现行数骤增/骤减及数据分布偏移的智能侦测",
              "无缝集成企业通讯与工单平台，实现故障一键派单与值班认领"
            ]
          },
          {
            name: "运维流程固化与规范推广",
            duration: "3 周",
            activities: [
              "编制常见数据故障的标准应急处置预案手册 (Runbooks)",
              "面向全体数据开发人员开展故障排查演练与应急流程实训",
              "设立常态化数据质量周度复盘会，将质量指标纳入团队核心考核"
            ]
          }
        ]
      },
      impact: {
        title: "项目成效与业务收益",
        description: "本方案彻底扭转了‘被动救火’的尴尬局面，将数据质量管控关口前移至上游，真正实现了从源头预防事故。",
        metrics: [
          { value: "75%", label: "严重数据质量事故发生率下降" },
          { value: "95%", label: "数据异常在上游生产前被拦截" },
          { value: "4小时", label: "数据故障平均排查修复时长缩短" },
          { value: "30%", label: "业务团队对数据资产信任度提升" }
        ],
        outcomes: [
          "重大数据故障数量骤降 75%，系统稳定性达到前所未有的高度",
          "战略分析与管理层决策恢复对数据的高度信赖",
          "团队彻底告别通宵救火排查脏数据的疲惫状态",
          "权责分明的责任闭环使得跨部门故障协同平均恢复时长 (MTTR) 显著压降"
        ]
      },
      learnings: [
        "预防永远胜于事后补救 —— 越早捕获数据异常，节省的人力与信任成本呈指数级倍增",
        "清晰的责任矩阵 (RACI) 是数据治理体系能够长效运行的根本制度保障",
        "详尽且可执行的标准处置预案，能将新人面对线上紧急事故的慌乱化为从容"
      ]
    },
    vi: {
      title: "Tự động hóa chất lượng dữ liệu",
      tagline: "Giảm đáng kể sự cố dữ liệu",
      tags: ["Dữ liệu", "Chất lượng", "Tự động hóa"],
      category: "Hạ tầng dữ liệu",
      duration: "4 tháng",
      role: "Quản lý sản phẩm",
      team: "4 kỹ sư dữ liệu, 1 kỹ sư nền tảng",
      summary: "Xây dựng khung chất lượng dữ liệu tự động giúp phát hiện sự cố trước khi ảnh hưởng đến các bên sử dụng phía sau, giảm 75% sự cố nghiêm trọng.",
      problem: {
        title: "Thách thức",
        description: "Các vấn đề dữ liệu chỉ lộ ra ở giai đoạn cuối của quy trình, gây lỗi báo cáo và làm suy giảm niềm tin. Các nhóm mất nhiều thời gian chữa cháy hơn là xây dựng.",
        painPoints: [
          "Sự cố dữ liệu nghiêm trọng bị phát hiện trên báo cáo production",
          "Không có cách tiếp cận có hệ thống để phát hiện bất thường sớm",
          "Trách nhiệm không rõ ràng khi sự cố xảy ra",
          "Niềm tin vào dữ liệu suy giảm trong toàn tổ chức"
        ]
      },
      approach: {
        title: "Cách tiếp cận",
        description: "Tôi thiết kế một khung chất lượng dữ liệu chủ động với các bước kiểm tra tự động, SLA rõ ràng và cơ cấu trách nhiệm cụ thể.",
        phases: [
          {
            name: "Đánh giá hiện trạng",
            duration: "2 tuần",
            activities: [
              "Lập danh mục các luồng dữ liệu quan trọng và mối phụ thuộc",
              "Phân tích lịch sử sự cố để nhận diện các mẫu lặp lại",
              "Xác định các khía cạnh chất lượng và thứ tự ưu tiên"
            ]
          },
          {
            name: "Thiết kế khung",
            duration: "3 tuần",
            activities: [
              "Xây dựng khung SLI/SLO cho chất lượng dữ liệu",
              "Thiết kế mẫu kiểm tra cho các tình huống phổ biến",
              "Xây dựng mô hình chuyển cấp và phân định trách nhiệm"
            ]
          },
          {
            name: "Triển khai",
            duration: "8 tuần",
            activities: [
              "Triển khai kiểm tra tự động trên toàn bộ pipeline",
              "Áp dụng các thuật toán phát hiện bất thường",
              "Xây dựng hệ thống cảnh báo và quản lý sự cố"
            ]
          },
          {
            name: "Vận hành hóa",
            duration: "3 tuần",
            activities: [
              "Soạn runbook cho các sự cố thường gặp",
              "Đào tạo chủ sở hữu dữ liệu về quy trình xử lý",
              "Thiết lập buổi đánh giá chất lượng hằng tuần"
            ]
          }
        ]
      },
      impact: {
        title: "Tác động",
        description: "Khung giải pháp đã chuyển công tác chất lượng dữ liệu từ chữa cháy bị động sang phòng ngừa chủ động.",
        metrics: [
          { value: "75%", label: "Giảm sự cố nghiêm trọng" },
          { value: "95%", label: "Sự cố được phát hiện trước production" },
          { value: "4 giờ", label: "Xử lý sự cố nhanh hơn" },
          { value: "30%", label: "Tăng điểm tin cậy dữ liệu" }
        ],
        outcomes: [
          "Giảm 75% sự cố chất lượng dữ liệu nghiêm trọng",
          "Tăng niềm tin vào phân tích dữ liệu cho các quyết định chiến lược",
          "Giải phóng các nhóm khỏi việc chữa cháy dữ liệu bị động, đột xuất",
          "Mô hình trách nhiệm rõ ràng giúp xử lý sự cố nhanh hơn"
        ]
      },
      learnings: [
        "Phòng bệnh hơn chữa bệnh — phát hiện sớm giúp tiết kiệm vô số giờ làm việc",
        "Quyền sở hữu rõ ràng là điều kiện thiết yếu để đảm bảo trách nhiệm",
        "Runbook tự động giúp giảm đáng kể MTTR"
      ]
    }
  },
  {
    id: "indie-saas",
    slug: "indie-saas-mvp",
    title: "Indie SaaS MVP",
    tagline: "Shipped in weeks, validated demand",
    tags: ["Indie", "SaaS", "MVP"],
    category: "Indie Hacking",
    duration: "6 weeks",
    role: "Solo Founder",
    team: "Just me + AI assistants",
    summary: "Built and launched a micro-SaaS product in 6 weeks to validate market demand, proving willingness-to-pay before investing in full development.",
    problem: {
      title: "The Challenge",
      description: "Creators lacked a lightweight way to validate paid communities before overbuilding. Most spent months building before discovering no one would pay.",
      painPoints: [
        "Creators building for months without validation",
        "Existing tools too complex for simple validation",
        "No clear playbook for testing willingness-to-pay",
        "High risk of building features no one needs"
      ]
    },
    approach: {
      title: "The Approach",
      description: "I applied indie hacker principles: ship fast, validate demand, iterate based on real user behavior.",
      phases: [
        {
          name: "Idea Validation",
          duration: "1 week",
          activities: [
            "Interviewed 15 potential users",
            "Analyzed competitor gaps",
            "Defined minimum viable scope"
          ]
        },
        {
          name: "Rapid Build",
          duration: "3 weeks",
          activities: [
            "Built core features with no-code + code hybrid",
            "Integrated Stripe for immediate monetization",
            "Created simple but polished UI"
          ]
        },
        {
          name: "Launch & Learn",
          duration: "2 weeks",
          activities: [
            "Launched on Product Hunt and Twitter",
            "Instrumented full funnel analytics",
            "Conducted user interviews with early adopters"
          ]
        }
      ]
    },
    impact: {
      title: "The Impact",
      description: "Validated the concept in weeks instead of months, with real revenue and clear signal on what to build next.",
      metrics: [
        { value: "$2K", label: "MRR in first month" },
        { value: "50+", label: "Paying customers" },
        { value: "40%", label: "Week 1 activation rate" },
        { value: "6wks", label: "Time to first revenue" }
      ],
      outcomes: [
        "Validated willingness-to-pay in weeks, not months",
        "Identified critical features vs. nice-to-haves early",
        "Built a repeatable playbook for future indie ideas",
        "Proved concept before major time investment"
      ]
    },
    techStack: ["Next.js", "Supabase", "Stripe", "Vercel", "TailwindCSS"],
    learnings: [
      "Ship embarrassingly early — users care about value, not polish",
      "Charge from day one to validate real demand",
      "Build in public to attract early adopters"
    ],
    zh: {
      title: "独立出海 Micro-SaaS MVP",
      tagline: "数周极速交付，验证真实付费意愿",
      tags: ["独立出海", "Micro-SaaS", "精益MVP"],
      category: "独立出海与创新孵化",
      duration: "6 周",
      role: "独立创作者 (Solo Founder)",
      team: "单兵作战 + AI 编程助手",
      summary: "在 6 周时间内全栈开发并上线了一款微型 SaaS 产品，在投入重度资源开发之前快速验证市场真实刚需与付费意愿。",
      problem: {
        title: "核心挑战与业务痛点",
        description: "许多出海创作者和数字游民在构思付费知识社群时，往往缺乏轻量低成本验证市场的手段，往往埋头研发数月上线后，才发现根本无人愿意为其掏钱包买单。",
        painPoints: [
          "开发者与创作者闭门造车数月，未做任何市场前置验证",
          "市面上现有成熟工具过于沉重繁琐，不适合做敏捷轻量测验",
          "缺乏一套经过实战检验、能快速测试真实付费意愿的敏捷方法论",
          "重度投入研发大量无人问津冗余功能的试错风险极高"
        ]
      },
      approach: {
        title: "解决方案与推行路径",
        description: "我坚定贯彻 Indie Hacker 独立出海极客原则：极速上线、验证真实付费意愿、基于真实付费用户的行为数据快速敏捷迭代。",
        phases: [
          {
            name: "需求验证与极简范围界定",
            duration: "1 周",
            activities: [
              "与 15 位目标海外创作者进行深度 1 对 1 线上调研",
              "横向拆解竞品核心优劣势，寻找尚未被很好满足的微小生态缝隙",
              "严格界定最小可行产品 (MVP) 的唯一核心功能边界"
            ]
          },
          {
            name: "全栈极速构建与打通支付",
            duration: "3 周",
            activities: [
              "借助 Modern Web 技术栈与 AI 编码助手，快速搭建端到端业务闭环",
              "首发日直接接入 Stripe 全球支付，第一天就开启收费验证",
              "打造极简现代、注重转化率与信任感的黑金质感 UI"
            ]
          },
          {
            name: "海外发布与漏斗复盘",
            duration: "2 周",
            activities: [
              "在 Product Hunt、Twitter (X) 及 Reddit 独立社群公开发布 (Build in Public)",
              "全流程埋点监控从落地页访问到最终 Stripe 绑卡付费的转化漏斗",
              "对前 20 位付费种子用户逐一访谈，收集一手体验反馈"
            ]
          }
        ]
      },
      impact: {
        title: "项目成效与业务收益",
        description: "仅用数周而非数月就验证了商业概念，获得了货真价实的现金流，并明确了下一阶段最值得投入的产品路线图。",
        metrics: [
          { value: "$2,000", label: "首月月经常性收入 (MRR)" },
          { value: "50+", label: "首批海外付费客户" },
          { value: "40%", label: "首周核心功能激活率" },
          { value: "6周", label: "从构想到产生第一笔美元收入" }
        ],
        outcomes: [
          "仅用数周时间成功验证了海外用户的真实付费意愿",
          "在极早期便厘清了‘用户真正需要的核心价值’与‘伪需求’的界限",
          "沉淀了一套可在未来反复复制的独立出海产品快速孵化流水线",
          "在投入大笔精力和资源前，以极小试错成本证明了商业闭环的可行性"
        ]
      },
      learnings: [
        "尽早交付哪怕不完美的产品 —— 用户真正买单的是核心价值，而非无休止的表面修饰",
        "从第一天起就敢于收费，付费才是检验商业刚需唯一的试金石",
        "坚持公开构建 (Build in Public)，天然能吸引同频的早期种子拥趸"
      ]
    },
    vi: {
      title: "MVP SaaS độc lập",
      tagline: "Ra mắt trong vài tuần, xác thực nhu cầu thực",
      tags: ["Indie", "SaaS", "MVP"],
      category: "Indie Hacking",
      duration: "6 tuần",
      role: "Nhà sáng lập độc lập",
      team: "Chỉ mình tôi + trợ lý AI",
      summary: "Xây dựng và ra mắt một sản phẩm micro-SaaS trong 6 tuần để xác thực nhu cầu thị trường, chứng minh khách hàng sẵn sàng trả tiền trước khi đầu tư phát triển toàn diện.",
      problem: {
        title: "Thách thức",
        description: "Các nhà sáng tạo nội dung thiếu một cách gọn nhẹ để xác thực cộng đồng trả phí trước khi xây dựng quá mức. Phần lớn mất hàng tháng xây dựng rồi mới phát hiện không ai sẵn sàng trả tiền.",
        painPoints: [
          "Nhà sáng tạo xây dựng hàng tháng trời mà không hề xác thực",
          "Các công cụ hiện có quá phức tạp cho nhu cầu xác thực đơn giản",
          "Không có quy trình rõ ràng để kiểm chứng mức độ sẵn sàng chi trả",
          "Rủi ro cao khi xây dựng những tính năng không ai cần"
        ]
      },
      approach: {
        title: "Cách tiếp cận",
        description: "Tôi áp dụng các nguyên tắc indie hacker: ra mắt nhanh, xác thực nhu cầu, lặp lại dựa trên hành vi thực tế của người dùng.",
        phases: [
          {
            name: "Xác thực ý tưởng",
            duration: "1 tuần",
            activities: [
              "Phỏng vấn 15 người dùng tiềm năng",
              "Phân tích khoảng trống của đối thủ cạnh tranh",
              "Xác định phạm vi khả thi tối thiểu"
            ]
          },
          {
            name: "Xây dựng nhanh",
            duration: "3 tuần",
            activities: [
              "Xây dựng tính năng cốt lõi bằng cách kết hợp no-code và code",
              "Tích hợp Stripe để kiếm tiền ngay lập tức",
              "Tạo giao diện đơn giản nhưng chỉn chu"
            ]
          },
          {
            name: "Ra mắt & học hỏi",
            duration: "2 tuần",
            activities: [
              "Ra mắt trên Product Hunt và Twitter",
              "Gắn công cụ đo lường cho toàn bộ phễu chuyển đổi",
              "Phỏng vấn những người dùng đầu tiên"
            ]
          }
        ]
      },
      impact: {
        title: "Tác động",
        description: "Xác thực ý tưởng chỉ trong vài tuần thay vì vài tháng, với doanh thu thực và tín hiệu rõ ràng về những gì cần xây dựng tiếp theo.",
        metrics: [
          { value: "$2K", label: "MRR trong tháng đầu tiên" },
          { value: "50+", label: "Khách hàng trả phí" },
          { value: "40%", label: "Tỷ lệ kích hoạt tuần đầu" },
          { value: "6 tuần", label: "Thời gian đến doanh thu đầu tiên" }
        ],
        outcomes: [
          "Xác thực mức độ sẵn sàng chi trả trong vài tuần thay vì vài tháng",
          "Sớm phân biệt tính năng thiết yếu với tính năng “có thì tốt”",
          "Xây dựng quy trình có thể lặp lại cho các ý tưởng indie sau này",
          "Chứng minh ý tưởng trước khi đầu tư nhiều thời gian"
        ]
      },
      learnings: [
        "Ra mắt thật sớm, dù chưa hoàn hảo — người dùng quan tâm đến giá trị, không phải sự bóng bẩy",
        "Thu phí ngay từ ngày đầu để xác thực nhu cầu thực",
        "Xây dựng công khai (build in public) để thu hút người dùng sớm"
      ]
    }
  }
];

const LOCALIZED_PROJECT_FIELDS = [
  "title",
  "tagline",
  "tags",
  "category",
  "duration",
  "role",
  "team",
  "summary",
  "problem",
  "approach",
  "impact",
  "learnings",
];

const localizeProject = (project, lang) => {
  const localized = lang !== 'en' ? project[lang] : null;
  if (!localized) return project;
  const result = { ...project };
  LOCALIZED_PROJECT_FIELDS.forEach((field) => {
    result[field] = localized[field] || project[field];
  });
  return result;
};

export const getProjectsData = (lang = 'en') => {
  return projectsData.map(project => localizeProject(project, lang));
};

export const getProjectBySlug = (slug, lang = 'en') => {
  const project = projectsData.find(p => p.slug === slug);
  if (!project) return undefined;
  return localizeProject(project, lang);
};

export const getProjectById = (id, lang = 'en') => {
  const project = projectsData.find(p => p.id === id);
  if (!project) return undefined;
  return localizeProject(project, lang);
};
